/** GxRoomScreen preview (screens §F6, F8): the projector at 16:9, presenter-dark. Never a supervisory number, never a value on the pipeline dots. */
import type { RoomScreenView } from "@bsim/contracts";
import { useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { displayRank, formatUsdPerShare, ordinal } from "../format";
import { SlideData } from "../room/Content";
import { Stage } from "../room/Finale";
import { stateHeadline } from "../room/RoomScreenPage";
import { GATE_RANK_REASON } from "../score/labels";
import { FINALE_LABELS, PICK_LABELS } from "../room/labels";
import { sentenceCase } from "../format/labels";
import { pickHeadline, pickPrompt } from "../room/pickWords";
export { PICK_LABELS };
import "../room/room.css";
import { formatCountdown } from "../shell/RoundClock";
import { useNow } from "../shell/useNow";
import { PIPELINE_WORDS, STATE_WORDS } from "./derive";
import { roundDone } from "./words";


const placeWords = (place: number | string): string => (typeof place === "number" ? ordinal(place) : place);

const DOT: Readonly<Record<RoomScreenView["pipeline"][number]["stage"], string>> = {
  notStarted: "muted", forecastCommitted: "muted", drafting: "warn", withCro: "warn", signed: "good",
};

/** The room's own content drawn at 1920 wide and scaled to the card: one source, so the preview cannot drift and nothing overlaps at the preview size. */
function ScaledStage({ testId, className = "", children }: { readonly testId: string; readonly className?: string; readonly children: ReactNode }) {
  const box = useRef<HTMLDivElement>(null);
  const inner = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(0.2);
  useLayoutEffect(() => {
    const el = box.current, content = inner.current;
    if (el === null || content === null) return;
    // The width sets the scale, but never past what lets the whole slide fit the card's height: six cards are shown, none cut off.
    const fit = () => { if (el.clientWidth > 0) setScale(Math.min(el.clientWidth / 1920, content.offsetHeight > 0 && el.clientHeight > 0 ? el.clientHeight / content.offsetHeight : Infinity)); };
    fit();
    if (typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(fit);
    ro.observe(el); ro.observe(content);
    return () => ro.disconnect();
  }, []);
  return (
    <div ref={box} className={`gx-fac-room-stage ${className}`} data-testid={testId}>
      <div ref={inner} className="gx-room-main gx-room-finale gx-fac-room-stage-in" style={{ transform: `scale(${scale})` }}>{children}</div>
    </div>
  );
}

function Body({ view, now, cohortId }: { readonly view: RoomScreenView; readonly now: number; readonly cohortId: string }) {
  const c = view.content;
  switch (c.kind) {
    case "pick": return <><div className="gx-fac-room-k">Round {c.pick.roundNo} · {PICK_LABELS[c.pick.kind]} · {c.pick.teamName}</div><div className="gx-fac-room-h">{pickHeadline(c.pick)}</div><div className="gx-fac-room-q">{pickPrompt(c.pick)}</div></>;
    case "slide": return <><div className="gx-fac-room-k">Slide {c.slide.slideNo} of 6</div><div className="gx-fac-room-h">{c.slide.title}</div><div className="gx-fac-room-q">{c.slide.prompt}</div><ScaledStage testId="room.preview.slide" className="gx-fac-room-stage--slide"><SlideData cohortId={cohortId} slide={c.slide} /></ScaledStage></>;
    case "surprise": return <><div className="gx-fac-room-k">{c.card.category} · live now</div><div className="gx-fac-room-h">{sentenceCase(c.card.title)}</div><div className="gx-fac-room-q">{c.card.narrative[0] ?? ""}</div></>;
    case "finale":
      // The full table drawn at 340 px is micro-text: the preview lists every bank in one readable line each.
      if (c.finale.stage === "rank" && c.finale.ranking !== null) {
        const rows = [...c.finale.ranking].sort((a, b) => (a.rank ?? Infinity) - (b.rank ?? Infinity));
        return <><div className="gx-fac-room-k">Finale</div><div className="gx-fac-room-h">{FINALE_LABELS.rank}</div>
          <ol className="gx-fac-room-list gx-fac-room-rank" data-testid="room.preview.rank">{rows.map((r) => <li key={r.teamId}><span>{placeWords(displayRank(r))}</span><b>{r.name}{r.gateRankedLast ? <small data-testid="room.preview.rank.gate">{GATE_RANK_REASON}</small> : null}</b><span className="gx-num">{r.score === null ? "" : formatUsdPerShare(r.score.valueCreatedUsdPerShare, { signed: true })}</span></li>)}</ol>
        </>;
      }
      return <><div className="gx-fac-room-k">Finale</div><div className="gx-fac-room-h">{FINALE_LABELS[c.finale.stage]}</div><ScaledStage testId="room.preview.finale"><Stage cohortId={cohortId} finale={c.finale} /></ScaledStage></>;
    case "none": {
      const { clock } = view;
      const ms = clock.countdownEndsAt === null ? null : Date.parse(clock.countdownEndsAt) - now;
      return <><div className="gx-fac-room-k">{clock.roundNo === null ? "Waiting for round 1" : `Round ${clock.roundNo}`}{clock.state === null ? "" : ` · ${STATE_WORDS[clock.state]}`}</div><div className="gx-fac-room-h gx-num">{clock.paused ? "Paused" : ms === null ? stateHeadline(clock) : formatCountdown(ms)}</div></>;
    }
  }
}

export function RoomPreview({ view }: { readonly view: RoomScreenView }) {
  const now = useNow();
  return (
    <div className="gx-fac-room" data-surface="projector" data-testid="room.preview" data-content={view.content.kind} {...(view.content.kind === "finale" ? { "data-stage": view.content.finale.stage } : {})}>
      <Body view={view} now={now} cohortId={view.cohortId} />
      {view.content.kind === "finale" ? null : (
        <div className="gx-fac-room-dots">
          {view.pipeline.map((p) => <span key={p.teamId} title={view.clock.roundNo !== null && view.clock.state !== null && roundDone({ state: view.clock.state }) ? `Round ${view.clock.roundNo} complete` : PIPELINE_WORDS[p.stage]}><i data-tone={DOT[p.stage]} />{p.name.replace(/^Team /, "")}</span>)}
        </div>
      )}
    </div>
  );
}
